/**
 * @file ui/fields/TextField.tsx
 * @description 使用 Ant Design Form.Item 封装的文本输入字段（垂直布局）。
 */

import type { ErrorKey } from '@core/validation/errorKeys'
import { getReadonlyTextControlProps } from '@ui/helpers/readonlyDisplay'
import { useT } from '@ui/i18n/useT'
import { useMemoizedFn } from 'ahooks'
import { ConfigProvider, Form, Input } from 'antd'
import type { ChangeEvent } from 'react'

import { resolveErrorMessage } from './error'
import { buildFormItemLayout, type FieldFormLayout, type FieldFormLayoutOptions } from './formItemLayout'

interface TextFieldProps {
  value?: string
  onChange?: (value: string) => void
  label?: string
  placeholder?: string
  hint?: string
  required?: boolean
  disabled?: boolean
  error?: ErrorKey
  multiline?: boolean
  rows?: number
  fieldPath?: string
  className?: string
  style?: React.CSSProperties
  /** HTML autocomplete 属性，用于浏览器自动填充 */
  autoComplete?: string
  /** 是否启用拼写检查，邮箱等字段应设为 false */
  spellCheck?: boolean
  formLayout?: FieldFormLayout
  labelWidth?: FieldFormLayoutOptions['labelWidth']
}

/**
 * TextField：使用 Ant Design Form.Item 的文本输入组件（垂直布局）。
 */
export function TextField({
  value,
  onChange,
  label,
  placeholder,
  hint,
  required,
  disabled,
  error,
  multiline,
  rows = 3,
  fieldPath,
  className,
  style,
  autoComplete,
  spellCheck,
  formLayout,
  labelWidth,
}: TextFieldProps) {
  const { t } = useT()
  const { componentDisabled } = ConfigProvider.useConfig()
  const isFieldDisabled = disabled || componentDisabled

  const resolvedPlaceholder = placeholder ?? t('placeholders.input')

  const handleChange = useMemoizedFn(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange?.(e.target.value)
    }
  )

  const errorText = resolveErrorMessage(t, error)
  const validateStatus = errorText ? 'error' : undefined
  const formItemLayout = buildFormItemLayout({ formLayout, labelWidth })

  // 必填字段的黄色背景样式
  const inputClassName = required && !isFieldDisabled && !value?.trim() ? 'field-required-empty' : undefined

  return (
    <Form.Item
      {...formItemLayout}
      label={label}
      required={required}
      validateStatus={validateStatus}
      help={errorText}
      extra={hint}
      data-field-path={fieldPath}
      className={className}
      style={style}
    >
      {multiline ? (
        <Input.TextArea
          value={value || undefined}
          onChange={handleChange}
          disabled={isFieldDisabled}
          rows={rows}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          {...getReadonlyTextControlProps({
            value,
            placeholder: resolvedPlaceholder,
            disabled: isFieldDisabled,
            className: inputClassName,
            ellipsis: false,
          })}
        />
      ) : (
        <Input
          value={value || undefined}
          onChange={handleChange}
          disabled={isFieldDisabled}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          {...getReadonlyTextControlProps({
            value,
            placeholder: resolvedPlaceholder,
            disabled: isFieldDisabled,
            className: inputClassName,
          })}
        />
      )}
    </Form.Item>
  )
}
